import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Phone, Mail, Camera, Plus, Trash2, Edit3, X, Sparkles, Clock as ClockIcon } from "lucide-react";

const blank = { name: "", role: "", phone: "", email: "", after_hours: false, notes: "" };

const ROLE_SUGGESTIONS = [
 "Property Manager", "Real Estate Agency", "Landlord",
 "Emergency Plumber", "Electrician", "Gas Emergency",
 "Pest Control", "Locksmith", "Building Manager",
 "Strata / Body Corporate", "Internet / NBN", "Other"
];

export default function ContactsTab({ caseId }) {
 const [contacts, setContacts] = useState([]);
 const [editing, setEditing] = useState(null);
 const [form, setForm] = useState(blank);
 const [extracting, setExtracting] = useState(false);
 const fileRef = useRef();

 const load = () => api.get(`/cases/${caseId}/contacts`).then(({ data }) => setContacts(data));
 // eslint-disable-next-line react-hooks/exhaustive-deps
 useEffect(() => { load(); }, [caseId]);

 const submit = async (e) => {
 e.preventDefault();
 if (editing === "new") {
 await api.post(`/cases/${caseId}/contacts`, form);
 } else {
 await api.put(`/contacts/${editing}`, form);
 }
 setEditing(null); setForm(blank); load();
 };

 const remove = async (id) => {
 if (!window.confirm("Delete this contact?")) return;
 await api.delete(`/contacts/${id}`);
 load();
 };

 const extract = async (file) => {
 setExtracting(true);
 try {
 const fd = new FormData();
 fd.append("file", file);
 const { data } = await api.post(`/cases/${caseId}/contacts/extract`, fd, {
 headers: { "Content-Type": "multipart/form-data" }
 });
 load();
 alert(`Extracted ${data.created} contact${data.created === 1 ? "" : "s"} from the image.`);
 } catch (e) {
 alert(e.response?.data?.detail || "Extraction failed");
 } finally {
 setExtracting(false);
 if (fileRef.current) fileRef.current.value = "";
 }
 };

 // Group contacts by category
 const grouped = contacts.reduce((acc, c) => {
 const key = c.role || "Other";
 (acc[key] = acc[key] || []).push(c);
 return acc;
 }, {});

 return (
 <div className="space-y-6" data-testid="contacts-tab">
 <div className="card bg-[#E6F5F2]/40">
 <div className="flex items-start gap-3">
 <Phone size={20} strokeWidth={1.5} className="text-[#1B6C62] mt-0.5" />
 <div className="flex-1">
 <h3 className="font-serif text-lg text-[#0D2B52] mb-1">Emergency contacts</h3>
 <p className="text-sm text-[#333] leading-relaxed">
 When the toilet's overflowing at 11pm, you don't want to be hunting through a pile of paper.
 Photograph the contact sheet your agent gave you on move-in and we'll pull the names + numbers
 automatically, or add them manually. Tap any number to call.
 </p>
 </div>
 </div>
 </div>

 <div className="flex flex-wrap items-center gap-3">
 <button onClick={() => fileRef.current?.click()} disabled={extracting} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="scan-contacts-btn">
 <Camera size={15} strokeWidth={1.5} /> {extracting ? "Reading…" : "Scan contacts sheet"}
 </button>
 <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && extract(e.target.files[0])} data-testid="scan-input" />
 {editing === null && (
 <button onClick={() => { setForm(blank); setEditing("new"); }} className="btn-secondary inline-flex items-center gap-2 text-sm" data-testid="add-contact-btn">
 <Plus size={15} strokeWidth={1.5} /> Add manually
 </button>
 )}
 <span className="label-mono ml-auto">{contacts.length} contact{contacts.length === 1 ? "" : "s"}</span>
 </div>

 {editing !== null && (
 <form onSubmit={submit} className="card space-y-4" data-testid="contact-form">
 <div className="flex justify-between items-center">
 <h3 className="font-serif text-xl">{editing === "new" ? "New contact" : "Edit contact"}</h3>
 <button type="button" onClick={() => { setEditing(null); setForm(blank); }} className="btn-ghost p-1.5"><X size={18} strokeWidth={1.5} /></button>
 </div>
 <div className="grid sm:grid-cols-2 gap-4">
 <div><label className="label-mono block mb-1.5">Name</label>
 <input required className="input-field" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="contact-name" /></div>
 <div><label className="label-mono block mb-1.5">Role</label>
 <input required list="roles" className="input-field" value={form.role} onChange={e=>setForm({...form,role:e.target.value})} data-testid="contact-role" />
 <datalist id="roles">{ROLE_SUGGESTIONS.map(r => <option key={r} value={r} />)}</datalist></div>
 <div><label className="label-mono block mb-1.5">Phone</label>
 <input type="tel" className="input-field" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} data-testid="contact-phone" /></div>
 <div><label className="label-mono block mb-1.5">Email</label>
 <input type="email" className="input-field" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} /></div>
 </div>
 <label className="flex items-center gap-2 text-sm">
 <input type="checkbox" checked={form.after_hours} onChange={e=>setForm({...form,after_hours:e.target.checked})} data-testid="contact-after-hours" />
 After-hours / emergency contact
 </label>
 <div><label className="label-mono block mb-1.5">Notes</label>
 <textarea rows={2} className="input-field" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} /></div>
 <div className="flex justify-end gap-2">
 <button type="button" onClick={() => { setEditing(null); setForm(blank); }} className="btn-secondary text-sm">Cancel</button>
 <button type="submit" className="btn-primary text-sm" data-testid="submit-contact">{editing === "new" ? "Add" : "Save"}</button>
 </div>
 </form>
 )}

 {contacts.length === 0 && editing === null && !extracting ? (
 <div className="text-sm text-[#6B7280] italic text-center py-8">No contacts yet. Take a photo of the move-in contacts sheet or add one manually.</div>
 ) : (
 <div className="space-y-6">
 {Object.entries(grouped).map(([role, items]) => (
 <div key={role}>
 <h4 className="label-mono mb-3">{role}</h4>
 <div className="grid sm:grid-cols-2 gap-3">
 {items.map(c => (
 <div key={c.id} className="card p-4" data-testid={`contact-${c.id}`}>
 <div className="flex justify-between items-start">
 <div className="min-w-0 flex-1">
 <div className="flex items-center gap-2 mb-1">
 <h4 className="font-medium text-[#1A1A1A] truncate">{c.name}</h4>
 {c.after_hours && <span className="text-[10px] uppercase tracking-wider bg-[#B35D4C]/10 text-[#B35D4C] px-1.5 py-0.5 rounded-sm flex items-center gap-1"><ClockIcon size={10} strokeWidth={2} /> 24/7</span>}
 </div>
 {c.phone && (
 <a href={`tel:${c.phone.replace(/\s/g,'')}`} className="flex items-center gap-1.5 text-sm text-[#0D2B52] hover:text-[#1B6C62]" data-testid={`call-${c.id}`}>
 <Phone size={13} strokeWidth={1.5} /> <span className="font-mono">{c.phone}</span>
 </a>
 )}
 {c.email && (
 <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 text-sm text-[#0D2B52] hover:text-[#1B6C62] mt-1">
 <Mail size={13} strokeWidth={1.5} /> {c.email}
 </a>
 )}
 {c.notes && <p className="text-xs text-[#6B7280] mt-2">{c.notes}</p>}
 </div>
 <div className="flex gap-1 flex-shrink-0">
 <button onClick={() => { setEditing(c.id); setForm({ name: c.name, role: c.role, phone: c.phone || "", email: c.email || "", after_hours: !!c.after_hours, notes: c.notes || "" }); }} className="btn-ghost p-1.5"><Edit3 size={13} strokeWidth={1.5} /></button>
 <button onClick={() => remove(c.id)} className="btn-ghost p-1.5 hover:text-[#B35D4C]" data-testid={`delete-contact-${c.id}`}><Trash2 size={13} strokeWidth={1.5} /></button>
 </div>
 </div>
 </div>
 ))}
 </div>
 </div>
 ))}
 </div>
 )}
 </div>
 );
}
